import DataTable from "react-data-table-component";
import { useEffect, useState } from "react";
import { getAllClients } from "../../api/clientApi";
import { Search, Filter, FileDown, User, Car, Calendar, CheckCircle, XCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import ClientDetailsModal from '../Modals/Client/ClientDetailsModal';
import { FaWhatsapp } from "react-icons/fa";

const ClientsTable = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFreeWash, setFilterFreeWash] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
// Add these state variables at the top with your other useState declarations:
const [activeStatFilter, setActiveStatFilter] = useState(null);



// Add this helper function to handle stat card clicks:
const handleStatClick = (filterType) => {
  if (activeStatFilter === filterType) {
    // If clicking the same filter, clear it
    setActiveStatFilter(null);
  } else {
    // Set new filter
    setActiveStatFilter(filterType);
  }
};
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const clients = await getAllClients();
        setData(clients);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Enhanced columns with more details
  const columns = [
    {
      name: "Client ID",
      selector: (row) => row.client_id,
      sortable: true,
      width: "100px",
      cell: (row) => <span className="font-mono text-sm">{row.client_id}</span>
    },
    {
      name: "Client Name",
      selector: (row) => row.username,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <User size={16} className="text-blue-600" />
          </div>
          <div>
            <div className="font-semibold text-sm">{row.username}</div>
            <div className="text-xs text-gray-500">{row.mobile}</div>
          </div>
        </div>
      )
    },
    {
      name: "Contact",
      selector: (row) => row.email,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div className="text-xs">
          <div>{row.email}</div>
          <div className="text-gray-500">{row.mobile}</div>
        </div>
      )
    },
    {
      name: "Vehicles",
      selector: (row) => row?.vehicle ? row.vehicle.length : 0,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Car size={16} className="text-purple-600" />
          <span className="font-semibold">{row?.vehicle ? row.vehicle.length : 0}</span>
        </div>
      )
    },
    {
      name: "Subscriptions",
      selector: (row) => row.clientSubscriptions ? row.clientSubscriptions.length : 0,
      sortable: true,
      width: "130px",
      cell: (row) => {
        const activeCount = row.clientSubscriptions?.filter(s => new Date(s.end_date) >new Date()).length || 0;
        const totalCount = row.clientSubscriptions?.length || 0;
        return (
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-green-600" />
            <span className="font-semibold">{activeCount}/{totalCount}</span>
            <span className="text-xs text-gray-500">active</span>
          </div>
        );
      }
    },
    {
      name: "Status",
      selector: (row) => row.subscription_status,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
          row.subscription_status === 'Active' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {row.subscription_status}
        </span>
      )
    },
    {
      name: "Free Wash",
      selector: (row) => row.free_wash_status,
      sortable: true,
      width: "110px",
      cell: (row) => (
        row.free_wash_status ? (
          <CheckCircle size={20} className="text-green-600" />
        ) : (
          <XCircle size={20} className="text-gray-400" />
        )
      )
    },
    {
      name: "Verified",
      selector: (row) => row.is_mobile_validated,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <div className="flex gap-1">
          {row.is_email_validated && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">📧</span>
          )}
          {row.is_mobile_validated && (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">📱</span>
          )}
        </div>
      )
    },
{
  name: "Expiring Soon",
  selector: (row) => row.date_created,
  sortable: true,
  width: "120px",
  cell: (row) => {
    // find the subscription with the earliest end date
    const sortedSubs = row.clientSubscriptions
      ?.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
    if (sortedSubs && sortedSubs.length > 0) {
      const nearestSub = sortedSubs[0];
      const endDate = new Date(nearestSub.end_date);
      return (
        <span className="text-xs">
          {endDate.toLocaleDateString('en-GB')}
        </span>
      );
    } else {
      return <span className="text-xs text-gray-500">N/A</span>;
    }
  }
},
    {
      name: "Actions",
      width: "150px",
      cell: (row) => {
        // pick active subscription if available, otherwise first subscription
        const currentSub = row.clientSubscriptions?.find(s => s.is_active) || row.clientSubscriptions?.[0];
        const subName = currentSub?.plan_name || currentSub?.name || 'Subscription';
        const start = currentSub?.start_date ? new Date(currentSub.start_date).toLocaleDateString() : 'N/A';
        const end = currentSub?.end_date ? new Date(currentSub.end_date).toLocaleDateString() : 'N/A';
        const formattedEnd = currentSub?.end_date ? new Date(currentSub.end_date).toLocaleDateString('en-GB') : 'N/A';
        const message = `Hello ${row.username} 
        Your subscription is active until ${formattedEnd}.
        Renewal will be done automatically to ensure your service continues without interruption.
        If for any reason it doesn't renew, you can renew it yourself by replying here or visiting gocleans.co 

        مرحباً ${row.username} 
        اشتراكك ساري حتى ${formattedEnd}.
        سيتم تجديد الاشتراك تلقائياً لضمان استمرار الخدمة دون انقطاع.
        وفي حال لم يتم التجديد تلقائياً، يمكنك التجديد بنفسك بالرد هنا أو من خلال الموقع gocleans.co `;
        const waLink = `https://wa.me/974${row.mobile}?text=${encodeURIComponent(message)}`;
        return (
          <div className="flex">
            <button
              onClick={() => setSelectedClient(row)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              View
            </button>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm ms-2"
            >
              <FaWhatsapp />
            </a>
          </div>
        );
      }
    }
  ];

  // Calculate statistics
// Replace the statistics calculation section with this:

const statistics = {
  totalClients: data.length,
  
  // Active subscriptions (end_date >= today)
  activeSubscriptions: data.filter(c => 
    c.clientSubscriptions?.some(sub => 
      new Date(sub.end_date) >= new Date() 
    )
  ).length,
  
  // Expired subscriptions (end_date < today)
  expiredSubscriptions: data.filter(c =>
    c.clientSubscriptions?.some(sub =>
      new Date(sub.end_date) < new Date()
    )
  ).length,
  
  // Expiring next week (end_date between today and 7 days from now)
  expiringNextWeek: data.filter(c => {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 1);
    return c.clientSubscriptions?.some(sub =>
      new Date(sub.end_date) >= today && 
      new Date(sub.end_date) <= nextWeek &&
      sub.is_active
    );
  }).length,
  
  totalVehicles: data.reduce((sum, c) => sum + (c.vehicle?.length || 0), 0),
  totalSubscriptions: data.reduce((sum, c) => sum + (c.clientSubscriptions?.length || 0), 0),
  completedProfiles: data.filter(c => c.is_profile_complete).length
};

  // Enhanced filtering
  // Update the filteredData logic to include stat filtering:
const filteredData = data.filter((item) => {
  const matchesText = 
    item.username.toLowerCase().includes(filterText.toLowerCase()) ||
    item.email.toLowerCase().includes(filterText.toLowerCase()) ||
    item.mobile.includes(filterText);
  
  const matchesStatus = filterStatus === "" || item.subscription_status === filterStatus;
  
  const matchesFreeWash = filterFreeWash === "" || 
    (filterFreeWash === "yes" && item.free_wash_status) ||
    (filterFreeWash === "no" && !item.free_wash_status);
  
  const matchesDate = (!startDate || new Date(item.date_created) >= new Date(startDate)) &&
                      (!endDate || new Date(item.date_created) <= new Date(endDate));

  // Add stat filter logic
  let matchesStatFilter = true;
  if (activeStatFilter) {
    const today = new Date();
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 1);

    switch(activeStatFilter) {
      case 'active':
        matchesStatFilter = item.clientSubscriptions?.some(sub => 
          new Date(sub.end_date) >= today && sub.is_active
        );
        break;
      case 'expired':
        matchesStatFilter = item.clientSubscriptions?.some(sub =>
          new Date(sub.end_date) < today
        );
        break;
      case 'expiring':
        matchesStatFilter = item.clientSubscriptions?.some(sub =>
          new Date(sub.end_date) >= today && 
          new Date(sub.end_date) <= nextWeek &&
          sub.is_active
        );
        break;
      case 'hasVehicles':
        matchesStatFilter = item.vehicle && item.vehicle.length > 0;
        break;
      case 'hasSubscriptions':
        matchesStatFilter = item.clientSubscriptions && item.clientSubscriptions.length > 0;
        break;
      default:
        matchesStatFilter = true;
    }
  }

  return matchesText && matchesStatus && matchesFreeWash && matchesDate && matchesStatFilter;
});

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredData.map(item => ({
      "Client ID": item.client_id,
      "Name": item.username,
      "Email": item.email,
      "Mobile": item.mobile,
      "Vehicles": item.vehicle?.length || 0,
      "Total Subscriptions": item.clientSubscriptions?.length || 0,
      "Active Subscriptions": item.clientSubscriptions?.filter(s => s.is_active).length || 0,
      "Subscription Status": item.subscription_status,
            "Expiring Soon": (() => {
        const sortedSubs = item.clientSubscriptions
          ?.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
        if (sortedSubs && sortedSubs.length > 0) {
          const nearestSub = sortedSubs[0];
          return new Date(nearestSub.end_date).toLocaleDateString('en-GB');
        } else {
          return 'N/A';
        }
      })(),
      "Free Wash": item.free_wash_status ? "Yes" : "No",
      "Email Verified": item.is_email_validated ? "Yes" : "No",
      "Mobile Verified": item.is_mobile_validated ? "Yes" : "No",
      "Profile Complete": item.is_profile_complete ? "Yes" : "No",
      "Date Joined": new Date(item.date_created).toLocaleDateString(),



    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients Report");
    
    ws['!cols'] = [
      { wch: 10 }, { wch: 20 }, { wch: 25 }, { wch: 15 },
      { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 18 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
    ];
    
    const fileName = `Clients_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.free_wash_status,
      style: {
        backgroundColor: "rgba(163, 195, 15, 0.1)",
        "&:hover": {
          backgroundColor: "rgba(163, 195, 15, 0.2)",
          cursor: "pointer",
        },
      },
    },
    {
      when: (row) => row.subscription_status === 'Active',
      style: {
        borderLeft: "4px solid #10b981",
      },
    },
  ];

  return (
    <div className="text-black w-full overflow-x-auto">
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-semibold">Error loading data:</p>
          <p>{error}</p>
        </div>
      ) : (
        <div className="mt-2">
         {/* Statistics Cards */}
<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
  <div 
    onClick={() => handleStatClick(null)}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === null 
        ? 'bg-blue-100 border-blue-800 shadow-md' 
        : 'bg-blue-50 border-blue-600'
    }`}
  >
    <p className="text-xs text-gray-600">Total Clients</p>
    <p className="text-2xl font-bold text-blue-600">{statistics.totalClients}</p>
    {activeStatFilter === null && <p className="text-xs text-blue-600 mt-1">● Active Filter</p>}
  </div>
  
  <div 
    onClick={() => handleStatClick('active')}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === 'active' 
        ? 'bg-green-100 border-green-800 shadow-md' 
        : 'bg-green-50 border-green-600'
    }`}
  >
    <p className="text-xs text-gray-600">Active Subscriptions</p>
    <p className="text-2xl font-bold text-green-600">{statistics.activeSubscriptions}</p>
    {activeStatFilter === 'active' && <p className="text-xs text-green-600 mt-1">● Active Filter</p>}
  </div>
  
  <div 
    onClick={() => handleStatClick('expired')}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === 'expired' 
        ? 'bg-red-100 border-red-800 shadow-md' 
        : 'bg-red-50 border-red-600'
    }`}
  >
    <p className="text-xs text-gray-600">Expired</p>
    <p className="text-2xl font-bold text-red-600">{statistics.expiredSubscriptions}</p>
    {activeStatFilter === 'expired' && <p className="text-xs text-red-600 mt-1">● Active Filter</p>}
  </div>
  
  <div 
    onClick={() => handleStatClick('expiring')}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === 'expiring' 
        ? 'bg-yellow-100 border-yellow-800 shadow-md' 
        : 'bg-yellow-50 border-yellow-600'
    }`}
  >
    <p className="text-xs text-gray-600">Expiring  Tomorrow</p>
    <p className="text-2xl font-bold text-yellow-600">{statistics.expiringNextWeek}</p>
    {activeStatFilter === 'expiring' && <p className="text-xs text-yellow-600 mt-1">● Active Filter</p>}
  </div>
  
  <div 
    onClick={() => handleStatClick('hasVehicles')}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === 'hasVehicles' 
        ? 'bg-purple-100 border-purple-800 shadow-md' 
        : 'bg-purple-50 border-purple-600'
    }`}
  >
    <p className="text-xs text-gray-600">Total Vehicles</p>
    <p className="text-2xl font-bold text-purple-600">{statistics.totalVehicles}</p>
    {activeStatFilter === 'hasVehicles' && <p className="text-xs text-purple-600 mt-1">● Active Filter</p>}
  </div>
  
  <div 
    onClick={() => handleStatClick('hasSubscriptions')}
    className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-lg ${
      activeStatFilter === 'hasSubscriptions' 
        ? 'bg-indigo-100 border-indigo-800 shadow-md' 
        : 'bg-indigo-50 border-indigo-600'
    }`}
  >
    <p className="text-xs text-gray-600">Total Subscriptions</p>
    <p className="text-2xl font-bold text-indigo-600">{statistics.totalSubscriptions}</p>
    {activeStatFilter === 'hasSubscriptions' && <p className="text-xs text-indigo-600 mt-1">● Active Filter</p>}
  </div>
</div>
{/* Active Filter Indicator */}
{activeStatFilter && (
  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
    <p className="text-sm text-blue-800">
      <span className="font-semibold">Active Filter:</span> {
        activeStatFilter === 'active' ? 'Active Subscriptions' :
        activeStatFilter === 'expired' ? 'Expired Subscriptions' :
        activeStatFilter === 'expiring' ? 'Expiring Next Week' :
        activeStatFilter === 'hasVehicles' ? 'Clients with Vehicles' :
        activeStatFilter === 'hasSubscriptions' ? 'Clients with Subscriptions' : ''
      }
    </p>
    <button 
      onClick={() => setActiveStatFilter(null)}
      className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
    >
      Clear Filter ✕
    </button>
  </div>
)}

          {/* Search and Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="flex flex-col md:flex-row gap-3 mb-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by name, email, or mobile..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                    focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                />
                <div className="absolute inset-y-0 start-0 flex items-center ps-3">
                  <Search className="w-4 h-4 text-gray-500" />
                </div>
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 
                  rounded-lg transition-colors"
              >
                <Filter size={18} />
                {showFilters ? "Hide" : "Show"} Filters
              </button>

              <button
                onClick={exportToExcel}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white 
                  rounded-lg hover:bg-green-700 transition-colors"
              >
                <FileDown size={18} />
                Export Excel
              </button>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-3 border-t">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  >
                    <option value="">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Expired">Expired</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Free Wash Status
                  </label>
                  <select
                    value={filterFreeWash}
                    onChange={(e) => setFilterFreeWash(e.target.value)}
                    className="border border-gray-300 rounded-lg p-2 text-sm w-full"
                  >
                    <option value="">All</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>
            )}

            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredData.length} of {data.length} clients
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <DataTable
              title="CLIENTS REPORT"
              data={filteredData}
              columns={columns}
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              fixedHeader={true}
              fixedHeaderScrollHeight="600px"
              conditionalRowStyles={conditionalRowStyles}
              highlightOnHover
              pointerOnHover
              dense
            />
          </div>
        </div>
      )}

      {/* Client Details Modal */}
      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}
    </div>
  );
};

export default ClientsTable;

// Send subscription reminder to whatsapp 